/** Project data hooks — spec §7.3. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomFieldType,
  ProjectRole,
  ProjectStatus,
  ProjectType,
  ProjectVisibility,
  StatusCategory,
  TaskType,
} from '@virtuetasks/types';
import { api, qs } from '@/lib/api';

export interface ProjectStatusView {
  id: string;
  name: string;
  category: StatusCategory;
  colour: string;
  order: number;
  wipLimit?: number | null;
  requiresReason?: boolean;
}

export interface ProjectView {
  _id: string;
  name: string;
  key: string;
  description?: string;
  colour: string;
  icon: string;
  status: ProjectStatus;
  visibility: ProjectVisibility;
  leadId: string;
  teamIds: string[];
  members: { userId: string; projectRole: ProjectRole; addedAt: string }[];
  startDate?: string;
  targetDate?: string;
  statuses: ProjectStatusView[];
  /** V4 F-1 — absent until someone fills it in. */
  about?: {
    summary?: Record<string, unknown>;
    projectType?: ProjectType;
    techStack: string[];
    links: { label: string; url: string }[];
    contacts: { userId: string; role: string }[];
  };
  customFields: { id: string; name: string; type: CustomFieldType; required: boolean; order: number }[];
  settings: {
    qaEnabled: boolean;
    requireReviewBeforeDone: boolean;
    allowGuestComments: boolean;
    defaultAssigneeId?: string | null;
    defaultTaskType: TaskType;
    autoArchiveDoneAfterDays?: number | null;
    wipEnforced: boolean;
    timeTrackingRequired: boolean;
  };
  stats: {
    totalTasks: number;
    openTasks: number;
    overdueTasks: number;
    blockedTasks: number;
    completedThisWeek: number;
  };
}

export const projectsKey = (filters: Record<string, unknown> = {}) => ['projects', filters] as const;
export const projectKey = (idOrKey: string) => ['project', idOrKey] as const;

export function useProjects(filters: { includeArchived?: boolean; q?: string } = {}) {
  return useQuery({
    queryKey: projectsKey(filters),
    queryFn: async () => (await api.get<ProjectView[]>(`/projects${qs(filters)}`)).data,
  });
}

export function useProject(idOrKey: string | undefined) {
  return useQuery({
    queryKey: projectKey(idOrKey ?? ''),
    queryFn: async () => (await api.get<ProjectView>(`/projects/${idOrKey}`)).data,
    enabled: Boolean(idOrKey),
  });
}

/** V4 F-1 — tags already used in the workspace, for the chip autocomplete. */
export function useTechStackSuggestions() {
  return useQuery({
    queryKey: ['tech-stack'],
    queryFn: async () => (await api.get<string[]>('/projects/tech-stack')).data,
  });
}

export function useProjectStats(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-stats', projectId],
    queryFn: async () => (await api.get<ProjectView['stats']>(`/projects/${projectId}/stats`)).data,
    enabled: Boolean(projectId),
  });
}

function useProjectInvalidation() {
  const qc = useQueryClient();
  return () => {
    // `refetchType: 'all'` matters here. Creating, deleting or archiving a
    // project is nearly always done from a screen the project list is not on,
    // so the list query is inactive; a plain invalidate marks it stale and
    // stops. React Query then serves the stale cache on mount and refetches
    // behind it, which showed a deleted project in the grid until a reload.
    void qc.invalidateQueries({ queryKey: ['projects'], refetchType: 'all' });
    void qc.invalidateQueries({ queryKey: ['project'], refetchType: 'all' });
  };
}

export function useCreateProject() {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: async (input: Record<string, unknown>) =>
      (await api.post<ProjectView>('/projects', input)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateProject() {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      (await api.patch<ProjectView>(`/projects/${id}`, patch)).data,
    onSuccess: invalidate,
  });
}

export function useReplaceStatuses() {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      statuses,
      migrateTo,
    }: {
      id: string;
      statuses: Partial<ProjectStatusView>[];
      migrateTo?: string;
    }) =>
      (
        await api.put<ProjectView>(
          `/projects/${id}/statuses${migrateTo ? `?migrateTo=${migrateTo}` : ''}`,
          { statuses },
        )
      ).data,
    onSuccess: invalidate,
  });
}

export function useArchiveProject() {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) =>
      (await api.post<ProjectView>(`/projects/${id}/${archived ? 'archive' : 'restore'}`)).data,
    onSuccess: invalidate,
  });
}

/** V4 F-3 — queues the zip; the link arrives as a notification. */
export function useExportProject() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ queued: boolean; message: string }>(`/projects/${id}/export`)).data,
  });
}

export interface DeletedProjectView {
  _id: string;
  key: string;
  name: string;
  icon: string;
  deletedAt: string | null;
  deletedBy: string | null;
  taskCount: number;
  purgeAt: string | null;
}

export function useDeletedProjects() {
  return useQuery({
    queryKey: ['deleted-projects'],
    retry: false,
    queryFn: async () => (await api.get<DeletedProjectView[]>('/projects/deleted')).data,
  });
}

export function useUndeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<ProjectView>(`/projects/${id}/undelete`)).data,
    onSuccess: () => {
      void qc.resetQueries({ queryKey: ['deleted-projects'] });
      void qc.resetQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, confirmKey }: { id: string; confirmKey: string }) =>
      (await api.delete<{ tasksSoftDeleted: number }>(`/projects/${id}`, { confirmKey })).data,
    onSuccess: (_result, { id }) => {
      // `reset`, not `invalidate`. Invalidation leaves the cached list in
      // place and refetches behind it, so the grid renders the project you
      // just deleted until the response lands — and if the query is inactive
      // at that moment, until the next full page load. Resetting discards the
      // data outright, so there is nothing stale left to paint.
      qc.removeQueries({ queryKey: ['project', id] });
      void qc.resetQueries({ queryKey: ['projects'] });
      void qc.resetQueries({ queryKey: ['deleted-projects'] });
    },
  });
}

export function useProjectMembers() {
  const invalidate = useProjectInvalidation();
  return {
    add: useMutation({
      mutationFn: async ({ id, userId, projectRole }: { id: string; userId: string; projectRole: ProjectRole }) =>
        (await api.post<ProjectView>(`/projects/${id}/members`, { userId, projectRole })).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, userId, projectRole }: { id: string; userId: string; projectRole: ProjectRole }) =>
        (await api.patch<ProjectView>(`/projects/${id}/members/${userId}`, { projectRole })).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async ({ id, userId }: { id: string; userId: string }) =>
        api.delete(`/projects/${id}/members/${userId}`),
      onSuccess: invalidate,
    }),
  };
}
